<template>
  <transition name="lpm-fade">
    <div v-if="show" class="lpm-overlay" @click.self="onCancel">
      <div class="lpm-modal md-elevation-10">
        <div class="lpm-header">
          <span class="lpm-title">{{ isEdit ? L.link_modal_edit_title : L.link_modal_title }}</span>
        </div>

        <div class="lpm-body">
          <label class="lpm-label">{{ L.link_modal_type }}</label>
          <CustomSelect :value="type" :options="typeOptions" @input="onTypeChange($event)" />

          <!-- variables that fit the chosen type ([userE] for e-mail, [userT] for
               phone, …) — picking one fills the value, free text stays possible -->
          <template v-if="variableOptions.length">
            <label class="lpm-label lpm-label--value">{{ L.link_modal_variable }}</label>
            <CustomSelect
                :value="selectedVariable"
                :options="variableOptions"
                :placeholder="L.link_modal_variable_placeholder"
                @input="onVariablePick($event)" />
          </template>

          <label class="lpm-label lpm-label--value">{{ valueLabel }}</label>
          <input
              ref="valueInput"
              class="lpm-input"
              type="text"
              :placeholder="valuePlaceholder"
              autocomplete="off"
              spellcheck="false"
              v-model="value"
              @keyup.enter="onConfirm" />
          <p class="lpm-hint">{{ valueHint }}</p>
        </div>

        <div class="lpm-actions">
          <button v-if="isEdit" class="lpm-btn lpm-btn--ghost" type="button" @click="onRemove">
            {{ L.link_modal_remove }}
          </button>
          <span class="lpm-spacer"></span>
          <button class="lpm-btn lpm-btn--secondary" type="button" @click="onCancel">
            {{ L.Abbrechen }}
          </button>
          <button class="lpm-btn lpm-btn--primary" type="button" :disabled="!value.trim()" @click="onConfirm">
            {{ L.Speichern }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * Link modal for the rich-text editors (TextEditorBox, TextEditorWithHeader).
 *
 * One button, three kinds of link: website (https://), e-mail (mailto:),
 * phone / fax (tel:). The value may be a placeholder ([userE], [userT]) which
 * the client / the e-mail replace with the travel expert's data.
 *
 * Props:
 *  - show: open/closed
 *  - options: app options (Lang)
 *  - initial: { type, value } to pre-fill (an existing link), or null
 *  - variables: { website: [{ token, label }], email: [...], phone: [...] } —
 *    the placeholders offered per type. Defaults to the travel-expert ones
 *    ([userE], [userT]) which every text (offer, e-mails) replaces; an editor
 *    whose texts support more (e.g. [customer_email]) passes its own list.
 *
 * Emits:
 *  - 'confirm' ({ type, value, href })
 *  - 'remove'  when editing an existing link and the user removes it
 *  - 'cancel'
 */
import CustomSelect from '../parts/CustomSelect.vue';
import { buildLinkHref } from '../../utils/linkHref';

export default {
  name: 'LinkPromptModal',
  components: { CustomSelect },
  props: {
    show: { type: Boolean, default: false },
    options: { type: Object, required: true },
    initial: { type: Object, default: null },
    variables: { type: Object, default: null }
  },
  data() {
    return {
      type: 'website',
      value: ''
    };
  },
  computed: {
    L() { return (this.options && this.options.Lang) || {}; },
    isEdit() { return !!(this.initial && this.initial.value); },
    typeOptions() {
      return [
        { value: 'website', label: this.L.link_type_website },
        { value: 'email', label: this.L.link_type_email },
        { value: 'phone', label: this.L.link_type_phone }
      ];
    },
    valueLabel() {
      return this.type === 'email' ? this.L.link_value_email
        : this.type === 'phone' ? this.L.link_value_phone
        : this.L.link_value_website;
    },
    valuePlaceholder() {
      return this.type === 'email' ? 'info@…' : this.type === 'phone' ? '+49 …' : 'https://…';
    },
    valueHint() {
      return this.type === 'email' ? this.L.link_hint_email
        : this.type === 'phone' ? this.L.link_hint_phone
        : this.L.link_hint_website;
    },
    // Variables for the current type, labelled with the same descriptions as
    // the variables table (Lang.variables).
    variableOptions() {
      const vars = this.L.variables || {};
      const defaults = {
        website: [],
        email: [{ token: '[userE]', label: vars.userE }],
        phone: [{ token: '[userT]', label: vars.userT }]
      };
      const list = (this.variables && this.variables[this.type]) || defaults[this.type] || [];
      return list.map(v => ({ value: v.token, label: v.label ? `${v.token} – ${v.label}` : v.token }));
    },
    selectedVariable() {
      const v = this.value.trim();
      return this.variableOptions.some(o => o.value === v) ? v : null;
    }
  },
  // vue-material's .md-app-container has transform:translate3D(0,0,0), which traps
  // position:fixed descendants (the overlay could never cover the sidebar). Portal
  // the element to <body> — same pattern as StripeKeyModal / globalConfirm.
  mounted() {
    document.body.appendChild(this.$el);
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  watch: {
    show(open) {
      if (!open) return;
      this.type = (this.initial && this.initial.type) || 'website';
      this.value = (this.initial && this.initial.value) || '';
      this.$nextTick(() => {
        if (this.$refs.valueInput) this.$refs.valueInput.focus();
      });
    }
  },
  methods: {
    onTypeChange(type) {
      this.type = type;
      this.$nextTick(() => {
        if (this.$refs.valueInput) this.$refs.valueInput.focus();
      });
    },
    onVariablePick(token) {
      this.value = token || '';
      this.$nextTick(() => {
        if (this.$refs.valueInput) this.$refs.valueInput.focus();
      });
    },
    onConfirm() {
      const href = buildLinkHref(this.type, this.value);
      if (!href) return;
      this.$emit('confirm', { type: this.type, value: this.value.trim(), href });
    },
    onRemove() {
      this.$emit('remove');
    },
    onCancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style scoped>
.lpm-overlay {
  position: fixed; inset: 0;
  /* Must beat the left sidebar (z-index 111111111) — same as StripeKeyModal. */
  z-index: 100000000001;
  background: rgba(20, 24, 33, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.lpm-modal {
  width: 480px; max-width: 100%;
  background: #fff; border-radius: 14px; overflow: visible;
  display: flex; flex-direction: column;
}
.lpm-header { padding: 1.5rem 1.75rem 0.5rem; }
.lpm-title { font-size: 1.2rem; font-weight: 700; color: #2c2c2c; }
.lpm-body { padding: 0.5rem 1.75rem 1rem; }

.lpm-label { display: block; font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 0.3rem; }
.lpm-label--value { margin-top: 1rem; }
.lpm-input {
  width: 100%; height: 44px; border: 1px solid #dcdcdc; border-radius: 8px;
  background: #fafafa; padding: 0 0.85rem; font-size: 0.9rem; color: #202224;
  box-sizing: border-box; transition: border-color .15s ease, background .15s ease;
}
.lpm-input:focus { outline: none; border-color: #2c2c2c; background: #fff; }
.lpm-hint { font-size: 0.78rem; color: #6b7280; margin: 0.45rem 0 0; line-height: 1.4; }

.lpm-actions {
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0.5rem 1.75rem 1.5rem;
}
.lpm-spacer { flex: 1; }
.lpm-btn {
  border: none; border-radius: 8px; padding: 0.6rem 1.1rem;
  font-size: 0.88rem; font-weight: 600; cursor: pointer; min-width: 110px;
}
.lpm-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.lpm-btn--primary { background: #2c2c2c; color: #fff; }
.lpm-btn--secondary { background: #eef1f5; color: #374151; }
.lpm-btn--ghost { background: transparent; color: #c0392b; min-width: 0; padding: 0.4rem 0; text-decoration: underline; }

.lpm-fade-enter-active, .lpm-fade-leave-active { transition: opacity .2s ease; }
.lpm-fade-enter, .lpm-fade-leave-to { opacity: 0; }
.lpm-fade-enter-active .lpm-modal { animation: lpm-pop .22s ease; }
@keyframes lpm-pop { from { transform: translateY(8px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
</style>
